import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FaDoorOpen, FaArrowRight } from "react-icons/fa";
import SEO from "../Components/SEO";

// Import your sub-components
import Story from "../Components/About/Story";
import VideoShowcase from "../Components/About/VideoShowcase";
import Vision from "../Components/About/Vision";
import Craftsmanship from "../Components/About/Craftsmanship";
import Certifications from "../Components/About/Certifications";
import DoorExplorer from "../Components/About/animation3d";



export default function AboutPage() {

  const navigate = useNavigate();
  const bgImage = "https://res.cloudinary.com/dtuqzt18e/image/upload/v1779694315/CDB-1101_inxvhc.png";

  return (
    <main style={{ backgroundColor: "#fff" }}>

      <SEO
        title="About Sree Raghavendra Doors & Plywood | Rajahmundry"
        description="Learn about Sree Raghavendra Doors, Plywood & Hardware in Rajahmundry. Founded by M. Venugopal in 2008 and led by Managing Director Koushik Mattapalli, providing quality building materials and doors across East Godavari."
        canonicalUrl="/about"
        keywords="about sree raghavendra, doors shop owner rajahmundry, venugopal doors rajamahendravaram, koushik mattapalli plywood, doors dealers history east godavari"
        ogImage="https://raghavendradoorsandhardware.in/assets/teak.png"
      />

      {/* --- HERO SECTION --- */}
      <section
        style={{
          height: "95vh",
          position: "relative",
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          backgroundColor: "#000", // Fallback color
        }}
      >
        {/* Animated Background Image (Ken Burns Effect) */}
        <motion.div
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 10, ease: "easeOut" }}
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url("${bgImage}")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "fixed", // Parallax effect
            zIndex: 1,
          }}
        />

        {/* Multi-layer Gradient Overlay for Depth */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to right, rgba(0,0,0,0.9) 30%, rgba(0,0,0,0.4) 70%, transparent 100%)",
            zIndex: 2,
          }}
        />

        <div className="container" style={{ position: "relative", zIndex: 10 }}>
          <div className="row">
            <div className="col-lg-7 col-md-10">

              {/* Floating Badge */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  background: "rgba(249, 115, 22, 0.15)",
                  border: "1px solid rgba(249, 115, 22, 0.3)",
                  padding: "8px 16px",
                  borderRadius: "50px",
                  width: "fit-content",
                  marginBottom: "2rem"
                }}
              >
                <FaDoorOpen color="#F97316" />
                <span style={{ color: "#F97316", fontWeight: "600", fontSize: "0.9rem", letterSpacing: "1px", textTransform: "uppercase" }}>
                  Redefining Entrances
                </span>
              </motion.div>

              {/* Headline */}
              <motion.h1
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                style={{
                  fontSize: "clamp(2.4rem, 6vw, 4rem)",
                  fontWeight: "800",
                  color: "white",
                  lineHeight: "1.1",
                  marginBottom: "1.5rem",
                  letterSpacing: "-2px"
                }}
              >
                Crafting <span style={{ color: "#F97316" }}>First Impressions</span> Since 2010
              </motion.h1>

              {/* Glassmorphism Description Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                style={{
                  background: "rgba(255, 255, 255, 0.05)",
                  backdropFilter: "blur(15px)",
                  padding: "2.5rem",
                  borderRadius: "24px",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
                }}
              >
                <p style={{ fontSize: "1.25rem", lineHeight: "1.8", color: "#cbd5e1", marginBottom: "2rem" }}>
                  We don't just build doors; we design gateways to your most cherished spaces.
                  Our premium collection blends artisanal craftsmanship with next-gen security.
                </p>

                <div className="d-flex flex-wrap gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05, backgroundColor: "#ea580c" }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate("/doors")}
                    style={{
                      background: "#F97316",
                      color: "white",
                      border: "none",
                      padding: "16px 35px",
                      borderRadius: "50px",
                      fontSize: "1rem",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      boxShadow: "0 10px 20px rgba(249, 115, 22, 0.3)",
                      cursor: "pointer",
                    }}
                  >
                    Explore Our Heritage <FaArrowRight />
                  </motion.button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Animated Scroll Down Indicator */}
        {/* <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          style={{
            position: "absolute",
            bottom: "30px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10,
            color: "white",
            textAlign: "center",
            cursor: "pointer"
          }}
        >
          <small style={{ display: "block", textTransform: "uppercase", letterSpacing: "2px", opacity: 0.6, marginBottom: "8px" }}>Scroll</small>
          <FaChevronDown size={20} color="#F97316" />
        </motion.div> */}
      </section>

      {/* --- CONTENT SECTIONS --- */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <Story />
        <Craftsmanship />
        <DoorExplorer />
        <VideoShowcase />
        <Vision />
        <Certifications />
      </motion.div>
    </main>
  );
}